import { useCallback, useEffect } from "react"

import { useRecoilState, useRecoilValue } from "recoil"
import { v4 as uuidv4 } from "uuid"

import { transpileTypeScript } from "../../../pages/plugins/utils/codeTranspiler"
import { getCustomViewTemplate } from "../../../pages/plugins/utils/customViewTemplate"
import { pluginStudioAtom } from "../atoms/pluginStudioAtom"
import { pluginApiEffects } from "../effects"
import {
  activeFileMetadataSelector,
  activeFileSelector,
  activePluginSelector,
  pluginStudioStateSelector,
} from "../selectors"
import {
  getCodeEditorPluginId,
  getLivePreviewPluginId,
  NEW_PLUGIN_ID_PREFIX,
  Plugin,
  PluginFile,
  setCodeEditorPluginId,
  setLivePreviewPluginId,
  toCodeEditorPluginId,
  toLivePreviewPluginId,
  UserRole,
} from "../types"

/**
 * Hook for managing plugins in the studio
 * This provides functionality for creating, editing, and saving plugins
 */
export function usePlugins() {
  const [state, setState] = useRecoilState(pluginStudioStateSelector)
  const activePlugin = useRecoilValue(activePluginSelector)
  const activeFile = useRecoilValue(activeFileSelector)

  // Log state changes for debugging
  useEffect(() => {
    const timestamp = new Date().toISOString()
    console.log(`[${timestamp}] Plugin Studio State Updated:`, {
      plugins: state.plugins.map((p) => ({ id: p.id, name: p.name })),
      codeEditorPluginId: getCodeEditorPluginId(state),
      activePluginState: getCodeEditorPluginId(state)
        ? state.pluginStates[getCodeEditorPluginId(state) as string]
        : null,
      testCasesCount: state.testCases.length,
      activeTestCaseId: state.activeTestCaseId,
      liveDataUsers: state.liveData.users.map((u) => ({
        id: u.id,
        name: u.name,
        role: u.role,
      })),
      activeLiveUserId: state.liveData.activeUserId,
      livePreviewPluginId: getLivePreviewPluginId(state),
      isOnCreationUrl: window.location.pathname.includes("creation"),
    })
  }, [state])

  // Create a default plugin template
  const createDefaultPlugin = useCallback(() => {
    const timestamp = Date.now()
    const pluginId = `${NEW_PLUGIN_ID_PREFIX}${timestamp}`

    // Simple default code templates
    const guestCode = `import React from 'react';

export default function GuestView() {
  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Guest View</h2>
      <p>This is the guest view of your plugin.</p>
    </div>
  );
}`

    const hostCode = `import React from 'react';

export default function HostView() {
  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Host View</h2>
      <p>This is the host view of your plugin.</p>
    </div>
  );
}`

    const hostConfigCode = `import React from 'react';

export default function hostConfigView() {
  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Host Config View</h2>
      <p>This is the host config view of your plugin.</p>
    </div>
  );
}`

    const newPlugin: Plugin = {
      id: pluginId,
      name: "New Plugin",
      description: "A new plugin created with Plugin Studio",
      manifest: {
        name: "New Plugin",
        description: "A new plugin created with Plugin Studio",
        version: "0.1.0",
        author: "Plugin Studio User",
        iconName: "lightning",
      },
      files: [
        {
          id: uuidv4(),
          name: "GuestView.jsx",
          path: "/GuestView.jsx",
          content: guestCode,
          type: UserRole.GUEST,
          lastModified: timestamp,
        },
        {
          id: uuidv4(),
          name: "HostView.jsx",
          path: "/HostView.jsx",
          content: hostCode,
          type: UserRole.HOST_EVENT_VIEW,
          lastModified: timestamp,
        },
        {
          id: uuidv4(),
          name: "hostConfigView.jsx",
          path: "/hostConfigView.jsx",
          content: hostConfigCode,
          type: UserRole.HOST_CONFIG_VIEW,
          lastModified: timestamp,
        },
      ],
      createdAt: timestamp,
      isNew: true,
    }

    return newPlugin
  }, [])

  // Initialize with a default plugin if none exist
  useEffect(() => {
    if (state.plugins.length === 0) {
      createNewPlugin()
    } else {
      // Make sure a plugin is selected
      if (!getCodeEditorPluginId(state) && state.plugins.length > 0) {
        selectPlugin(state.plugins[0].id)
      }
    }
  }, [state.plugins.length])

  // Ensure the guest file is selected by default for the active plugin
  useEffect(() => {
    const activePluginId = getCodeEditorPluginId(state)
    if (!activePluginId) return

    const plugin = state.plugins.find((p) => p.id === activePluginId)
    if (!plugin) return

    const pluginState = state.pluginStates[activePluginId as string]
    if (!pluginState || !pluginState.activeFileId) {
      // Find the guest file and select it
      const guestFile = plugin.files.find((f) => f.type === UserRole.GUEST)
      if (guestFile) {
        setActiveFile(activePluginId as string, guestFile.id)
      }
    }
  }, [getCodeEditorPluginId(state)])

  // Create a new plugin
  const createNewPlugin = useCallback(() => {
    const newPlugin = createDefaultPlugin()

    setState((currentState) => {
      // Use the setter functions to update both active plugin IDs
      const stateWithStudioId = setCodeEditorPluginId(
        currentState,
        newPlugin.id,
      )
      const stateWithBothIds = setLivePreviewPluginId(
        stateWithStudioId,
        newPlugin.id,
      )

      return {
        ...stateWithBothIds,
        plugins: [...currentState.plugins, newPlugin],
        pluginStates: {
          ...currentState.pluginStates,
          [newPlugin.id]: {
            activeFileId:
              newPlugin.files.find((f) => f.type === UserRole.GUEST)?.id ||
              null,
            openFileIds: [
              newPlugin.files.find((f) => f.type === UserRole.GUEST)?.id || "",
            ],
            activeViewType: UserRole.GUEST,
          },
        },
        isPreviewStale: true,
      }
    })

    return newPlugin.id
  }, [createDefaultPlugin])

  // Add a plugin from API data
  const addExistingPlugin = useCallback(
    (apiPlugin: any) => {
      console.log(
        "[usePlugins] Adding plugin from API:",
        apiPlugin.id,
        apiPlugin.manifest?.name,
      )

      // Create plugin files from code in API response
      const timestamp = Date.now()
      const files: PluginFile[] = []

      // Guest file
      if (
        apiPlugin.code?.frontendGuestCodeWeb ||
        apiPlugin.code?.transpiledFrontendGuestCodeWeb
      ) {
        files.push({
          id: `${apiPlugin.id}-guest`,
          name: "GuestView.tsx",
          path: `/frontend/GuestView.tsx`,
          content:
            apiPlugin.code?.frontendGuestCodeWeb ||
            apiPlugin.code?.transpiledFrontendGuestCodeWeb ||
            "",
          type: UserRole.GUEST,
          lastModified: timestamp,
        })
      }

      // Host file
      if (
        apiPlugin.code?.frontendHostViewCodeWeb ||
        apiPlugin.code?.transpiledFrontendHostViewCodeWeb
      ) {
        files.push({
          id: `${apiPlugin.id}-host`,
          name: "HostView.tsx",
          path: `/frontend/HostView.tsx`,
          content:
            apiPlugin.code?.frontendHostViewCodeWeb ||
            apiPlugin.code?.transpiledFrontendHostViewCodeWeb ||
            "",
          type: UserRole.HOST_EVENT_VIEW,
          lastModified: timestamp,
        })
      }

      // Host config file
      if (
        apiPlugin.code?.frontendHostConfigCodeWeb ||
        apiPlugin.code?.transpiledFrontendHostConfigCodeWeb
      ) {
        files.push({
          id: `${apiPlugin.id}-hostConfigView`,
          name: "hostConfigView.tsx",
          path: `/frontend/hostConfigView.tsx`,
          content:
            apiPlugin.code?.frontendHostConfigCodeWeb ||
            apiPlugin.code?.transpiledFrontendHostConfigCodeWeb ||
            "",
          type: UserRole.HOST_CONFIG_VIEW,
          lastModified: timestamp,
        })
      }

      // If no files were found, add empty placeholders
      if (files.length === 0) {
        files.push({
          id: `${apiPlugin.id}-guest`,
          name: "GuestView.tsx",
          path: `/frontend/GuestView.tsx`,
          content:
            "// Empty guest view\nexport default function GuestView() {\n  return <div>Guest View</div>;\n}",
          type: UserRole.GUEST,
          lastModified: timestamp,
        })
        files.push({
          id: `${apiPlugin.id}-host`,
          name: "HostView.tsx",
          path: `/frontend/HostView.tsx`,
          content:
            "// Empty host view\nexport default function HostView() {\n  return <div>Host View</div>;\n}",
          type: UserRole.HOST_EVENT_VIEW,
          lastModified: timestamp,
        })
      }

      // Create a proper Plugin object
      const plugin: Plugin = {
        id: apiPlugin.id,
        name: apiPlugin.manifest?.name || "Unnamed Plugin",
        description: apiPlugin.manifest?.description || "",
        manifest: {
          name: apiPlugin.manifest?.name || "Unnamed Plugin",
          description: apiPlugin.manifest?.description || "",
          version: apiPlugin.manifest?.version || "1.0.0",
          author: apiPlugin.manifest?.author || "Unknown",
          category: "custom",
          tags: apiPlugin.manifest?.searchTags || [],
          iconName: apiPlugin.manifest?.iconName || "lightning",
        },
        files: files,
        createdAt: apiPlugin.createdAt?._seconds
          ? apiPlugin.createdAt._seconds * 1000
          : timestamp,
      }

      // Add the plugin to state
      setState((currentState) => ({
        ...currentState,
        plugins: [...currentState.plugins, plugin],
        // Don't set as active plugin automatically
      }))

      console.log(
        "[usePlugins] Added plugin with files:",
        plugin.id,
        plugin.files.length,
      )

      return plugin.id
    },
    [setState],
  )

  // Update an existing plugin
  const updatePlugin = useCallback(
    (pluginId: string, updates: Partial<Plugin>) => {
      setState((currentState) => ({
        ...currentState,
        plugins: currentState.plugins.map((plugin) =>
          plugin.id === pluginId ? { ...plugin, ...updates } : plugin,
        ),
      }))
    },
    [],
  )

  // Delete a plugin
  const deletePlugin = useCallback((pluginId: string) => {
    setState((currentState) => {
      // If we're deleting the active plugin, set a new active plugin
      if (currentState.plugins.length <= 1) {
        return currentState
      }

      const newPlugins = currentState.plugins.filter(
        (plugin) => plugin.id !== pluginId,
      )

      // Check if we're deleting the active plugin
      const codeEditorPluginId = getCodeEditorPluginId(currentState)
      const livePreviewPluginId = getLivePreviewPluginId(currentState)

      // Update active plugin IDs if needed
      let updatedState = currentState

      if (codeEditorPluginId === pluginId) {
        updatedState = setCodeEditorPluginId(
          updatedState,
          newPlugins[0]?.id || null,
        )
      }

      if (livePreviewPluginId === pluginId) {
        updatedState = setLivePreviewPluginId(
          updatedState,
          newPlugins[0]?.id || null,
        )
      }

      // Remove the plugin state
      const { [pluginId]: _, ...restPluginStates } = updatedState.pluginStates

      return {
        ...updatedState,
        plugins: newPlugins,
        pluginStates: restPluginStates,
      }
    })
  }, [])

  // Select a plugin as active
  const selectPlugin = useCallback((pluginId: string) => {
    setState((currentState) => {
      // If it's already selected, do nothing
      if (getCodeEditorPluginId(currentState) === pluginId) {
        return currentState
      }

      // Get the plugin from state
      const plugin = currentState.plugins.find((p) => p.id === pluginId)

      // If plugin doesn't exist, don't change anything
      if (!plugin) {
        return currentState
      }

      // If the plugin doesn't have an editor state yet, initialize it
      const currentPluginState = currentState.pluginStates[pluginId]

      const guestFileId = plugin.files.find(
        (f) => f.type === UserRole.GUEST,
      )?.id

      const pluginState = currentPluginState || {
        activeFileId: guestFileId || null,
        openFileIds: guestFileId ? [guestFileId] : [],
        activeViewType: UserRole.GUEST,
      }

      // Update both active plugin IDs
      let updatedState = setCodeEditorPluginId(currentState, pluginId)
      updatedState = setLivePreviewPluginId(updatedState, pluginId)

      return {
        ...updatedState,
        pluginStates: {
          ...currentState.pluginStates,
          [pluginId]: pluginState,
        },
        isPreviewStale: true,
      }
    })
  }, [])

  // Update a file in a plugin
  const updateFile = useCallback(
    (pluginId: string, fileId: string, updates: Partial<PluginFile>) => {
      setState((currentState) => ({
        ...currentState,
        plugins: currentState.plugins.map((plugin) => {
          if (plugin.id !== pluginId) return plugin

          return {
            ...plugin,
            files: plugin.files.map((file) =>
              file.id === fileId
                ? { ...file, ...updates, lastModified: Date.now() }
                : file,
            ),
          }
        }),
        isPreviewStale: true,
      }))
    },
    [],
  )

  // Add a new file to a plugin
  const addFile = useCallback(
    (pluginId: string, file: Omit<PluginFile, "id" | "lastModified">) => {
      const newFileId = uuidv4()

      setState((currentState) => {
        const plugin = currentState.plugins.find((p) => p.id === pluginId)
        if (!plugin) return currentState

        const newFile: PluginFile = {
          ...file,
          id: newFileId,
          lastModified: Date.now(),
        }

        return {
          ...currentState,
          plugins: currentState.plugins.map((p) =>
            p.id === pluginId
              ? {
                  ...p,
                  files: [...p.files, newFile],
                }
              : p,
          ),
          isPreviewStale: true,
        }
      })

      return newFileId
    },
    [],
  )

  // Delete a file from a plugin
  const deleteFile = useCallback((pluginId: string, fileId: string) => {
    setState((currentState) => {
      const plugin = currentState.plugins.find((p) => p.id === pluginId)
      if (!plugin) return currentState

      // Don't delete if it's the only file
      if (plugin.files.length <= 1) return currentState

      // Update plugins
      const updatedPlugins = currentState.plugins.map((p) => {
        if (p.id !== pluginId) return p
        return {
          ...p,
          files: p.files.filter((f) => f.id !== fileId),
        }
      })

      // Update plugin state if needed
      let updatedPluginStates = { ...currentState.pluginStates }
      const pluginState = currentState.pluginStates[pluginId]

      if (pluginState) {
        // If the active file is being deleted, select another one
        let newActiveFileId = pluginState.activeFileId
        const newOpenFileIds = pluginState.openFileIds.filter(
          (id) => id !== fileId,
        )

        if (pluginState.activeFileId === fileId) {
          newActiveFileId = newOpenFileIds.length > 0 ? newOpenFileIds[0] : null
        }

        updatedPluginStates = {
          ...updatedPluginStates,
          [pluginId]: {
            ...pluginState,
            activeFileId: newActiveFileId,
            openFileIds: newOpenFileIds,
          },
        }
      }

      return {
        ...currentState,
        plugins: updatedPlugins,
        pluginStates: updatedPluginStates,
        isPreviewStale: true,
      }
    })
  }, [])

  // Set a file as active in the editor
  const setActiveFile = useCallback((pluginId: string, fileId: string) => {
    setState((currentState) => {
      const pluginState = currentState.pluginStates[pluginId]
      if (!pluginState) return currentState

      // If the file isn't in the open files, add it
      const openFileIds = pluginState.openFileIds.includes(fileId)
        ? pluginState.openFileIds
        : [...pluginState.openFileIds, fileId]

      // Find the file to get its type
      const plugin = currentState.plugins.find((p) => p.id === pluginId)
      const file = plugin?.files.find((f) => f.id === fileId)

      const viewType =
        file?.type === UserRole.GUEST ||
        file?.type === UserRole.HOST_EVENT_VIEW ||
        file?.type === UserRole.HOST_CONFIG_VIEW
          ? file.type
          : pluginState.activeViewType

      // Set studio active plugin ID
      const updatedState = setCodeEditorPluginId(currentState, pluginId)

      return {
        ...updatedState,
        pluginStates: {
          ...currentState.pluginStates,
          [pluginId]: {
            ...pluginState,
            activeFileId: fileId,
            openFileIds,
            activeViewType: viewType,
          },
        },
        isPreviewStale: true,
      }
    })
  }, [])

  // Close a file in the editor
  const closeFile = useCallback((pluginId: string, fileId: string) => {
    setState((currentState) => {
      const pluginState = currentState.pluginStates[pluginId]
      if (!pluginState) return currentState

      // Remove from open files
      const newOpenFileIds = pluginState.openFileIds.filter(
        (id) => id !== fileId,
      )

      // If it was the active file, select another one
      let newActiveFileId = pluginState.activeFileId
      if (pluginState.activeFileId === fileId) {
        newActiveFileId = newOpenFileIds.length > 0 ? newOpenFileIds[0] : null
      }

      return {
        ...currentState,
        pluginStates: {
          ...currentState.pluginStates,
          [pluginId]: {
            ...pluginState,
            activeFileId: newActiveFileId,
            openFileIds: newOpenFileIds,
          },
        },
        isPreviewStale: true,
      }
    })
  }, [])

  // Set the active view type (guest, host, hostconfig)
  const setActiveViewType = useCallback(
    (pluginId: string, viewType: UserRole) => {
      setState((currentState) => {
        const pluginState = currentState.pluginStates[pluginId] || {
          activeFileId: null,
          openFileIds: [],
          activeViewType: UserRole.GUEST,
        }

        // Find a file of the corresponding type to set as active
        const plugin = currentState.plugins.find((p) => p.id === pluginId)
        const fileOfType = plugin?.files.find((f) => f.type === viewType)

        // If we found a file of the type, set it as active
        let activeFileId = pluginState.activeFileId
        let openFileIds = [...pluginState.openFileIds]

        if (fileOfType) {
          activeFileId = fileOfType.id
          if (!openFileIds.includes(fileOfType.id)) {
            openFileIds = [...openFileIds, fileOfType.id]
          }
        }

        // Set studio active plugin ID
        const updatedState = setCodeEditorPluginId(currentState, pluginId)

        return {
          ...updatedState,
          pluginStates: {
            ...currentState.pluginStates,
            [pluginId]: {
              ...pluginState,
              activeFileId,
              openFileIds,
              activeViewType: viewType,
            },
          },
          isPreviewStale: true,
        }
      })
    },
    [],
  )

  // Mark preview as needing refresh
  const refreshPreview = useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      isPreviewStale: false,
    }))
  }, [])

  // Save plugin to server
  const saveToServer = useCallback(async () => {
    if (!activePlugin) {
      console.log("[usePlugins] No active plugin to save")
      return { success: false, error: "No active plugin to save" }
    }

    try {
      console.log("[usePlugins] Saving plugin to server:", activePlugin.id)

      // First, transpile all files that need transpilation
      const transpileResults = await Promise.all(
        activePlugin.files.map(async (file) => {
          // Skip files that already have transpiled content
          if (file.transpiledContent && file.transpiledContent.trim() !== "") {
            return { file, success: true }
          }

          console.log(`[usePlugins] Transpiling file: ${file.name}`)
          const result = await transpileTypeScript(file.content)
          return {
            file,
            success: result.success,
            transpiledCode: result.transpiledCode,
            error: result.error,
          }
        }),
      )

      // Check for transpilation errors
      const transpileErrors = transpileResults.filter(
        (result) => !result.success,
      )
      if (transpileErrors.length > 0) {
        const errorMessages = transpileErrors
          .map(
            (error) => `${error.file.name}: ${error.error || "Unknown error"}`,
          )
          .join("\n")
        console.error("[usePlugins] Transpilation errors:", errorMessages)
        return {
          success: false,
          error: `Failed to transpile some files:\n${errorMessages}`,
        }
      }

      // Update plugin with transpiled code
      const updatedPlugin = {
        ...activePlugin,
        files: activePlugin.files.map((file) => {
          const transpileResult = transpileResults.find(
            (r) => r.file.id === file.id,
          )
          return transpileResult?.transpiledCode
            ? { ...file, transpiledContent: transpileResult.transpiledCode }
            : file
        }),
        code: {
          ...activePlugin.code,
          transpiledFrontendGuestCodeWeb:
            transpileResults.find((r) => r.file.type === UserRole.GUEST)
              ?.transpiledCode ||
            activePlugin.code?.transpiledFrontendGuestCodeWeb ||
            "",
          transpiledFrontendHostViewCodeWeb:
            transpileResults.find(
              (r) => r.file.type === UserRole.HOST_EVENT_VIEW,
            )?.transpiledCode ||
            activePlugin.code?.transpiledFrontendHostViewCodeWeb ||
            "",
          transpiledFrontendHostConfigCodeWeb:
            transpileResults.find(
              (r) => r.file.type === UserRole.HOST_CONFIG_VIEW,
            )?.transpiledCode ||
            activePlugin.code?.transpiledFrontendHostConfigCodeWeb ||
            "",
        },
      }

      // Determine operation type based on the isNew flag or id pattern
      const isNew =
        updatedPlugin.isNew || updatedPlugin.id.startsWith(NEW_PLUGIN_ID_PREFIX)
      const operation = isNew ? "create" : "update"

      console.log(`[usePlugins] Operation type: ${operation} (isNew: ${isNew})`)

      // For create operations, don't send the temporary ID
      const pluginToSave = isNew
        ? {
            ...updatedPlugin,
            id: undefined, // Let the server assign an ID
          }
        : updatedPlugin

      // Call API to save the plugin
      const result = await pluginApiEffects.savePlugin(pluginToSave, operation)

      // First check if the operation was successful
      if (!result.success || !result.data) {
        console.error(
          "[usePlugins] Error saving plugin:",
          result.error || "No data returned",
        )
        return {
          success: false,
          error: result.error || "Failed to save plugin",
        }
      }

      console.log("[usePlugins] Plugin saved successfully:", result.data)

      // Handle server response
      const serverPlugin = result.data as { pluginId?: string }
      const newPluginId = serverPlugin.pluginId || (result.data as any)?.id

      if (isNew && newPluginId && newPluginId !== activePlugin.id) {
        console.log("[usePlugins] Replacing draft plugin with server plugin:", {
          oldId: activePlugin.id,
          newId: newPluginId,
        })

        // Update plugin with server-assigned ID and remove isNew flag
        const updatedPlugin = {
          ...activePlugin,
          id: newPluginId,
          isNew: false,
        }

        // Update state with the new plugin
        setState((currentState) => {
          // Remove the draft plugin
          const filteredPlugins = currentState.plugins.filter(
            (p) => p.id !== activePlugin.id,
          )

          // Set both active plugin IDs to the new server plugin ID
          let updatedState = setCodeEditorPluginId(currentState, newPluginId)
          updatedState = setLivePreviewPluginId(updatedState, newPluginId)

          // Move plugin state to new ID
          const oldPluginState = currentState.pluginStates[activePlugin.id]
          const updatedPluginStates = { ...currentState.pluginStates }
          if (oldPluginState) {
            delete updatedPluginStates[activePlugin.id]
            updatedPluginStates[newPluginId] = oldPluginState
          }

          return {
            ...updatedState,
            plugins: [...filteredPlugins, updatedPlugin],
            pluginStates: updatedPluginStates,
          }
        })
      } else if (isNew) {
        // If we didn't get a new ID but it was a create operation,
        // at least update the isNew flag
        updatePlugin(activePlugin.id, { isNew: false })
      }

      return { success: true, data: result.data }
    } catch (error) {
      console.error("[usePlugins] Error saving plugin:", error)
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      }
    }
  }, [activePlugin, setState, updatePlugin])

  // Create a new custom view
  const createCustomView = useCallback(
    (pluginId: string, viewName: string, visibleToRole: UserRole) => {
      const timestamp = Date.now()
      const fileId = uuidv4()
      const fileName = `${viewName}View.tsx`

      // Simple template for the new view
      const templateCode = getCustomViewTemplate(viewName, visibleToRole)

      setState((currentState) => {
        const plugin = currentState.plugins.find((p) => p.id === pluginId)
        if (!plugin) return currentState

        const newFile: PluginFile = {
          id: fileId,
          name: fileName,
          path: `/${fileName}`,
          content: templateCode,
          type: visibleToRole, // Use the role this view is visible to
          lastModified: timestamp,
        }

        return {
          ...currentState,
          plugins: currentState.plugins.map((p) =>
            p.id === pluginId
              ? {
                  ...p,
                  files: [...p.files, newFile],
                }
              : p,
          ),
          isPreviewStale: true,
        }
      })

      return fileId
    },
    [],
  )

  return {
    plugins: state.plugins,
    createNewPlugin,
    addExistingPlugin,
    updatePlugin,
    deletePlugin,
    selectPlugin,
    updateFile,
    addFile,
    deleteFile,
    setActiveFile,
    closeFile,
    setActiveViewType,
    refreshPreview,
    saveToServer,
    activePlugin,
    activeFile,
    isPreviewStale: state.isPreviewStale,
    createCustomView,
  }
}
